import * as THREE from "three";
import { WallType } from "../types/floorplannerTypes";

// ---------------------------------------------------------------------------
// Arc-length table cache: keyed by "wallId:geometryVersion" so it is
// invalidated automatically whenever the wall geometry changes.
// ---------------------------------------------------------------------------
const arcLengthTableCache = new Map<string, number[]>();

/**
 * Builds a cumulative arc-length table for a quadratic Bézier curve.
 * Returns an array of length (samples + 1) where table[i] is the arc length
 * from t=0 to t=i/samples.
 */
export function buildArcLengthTable(
  start: THREE.Vector2,
  controlPoint: THREE.Vector2,
  end: THREE.Vector2,
  samples = 100,
): number[] {
  const table: number[] = new Array(samples + 1);
  table[0] = 0;
  let prev = start.clone();
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const pt = getQuadraticBezierPoint(t, start, controlPoint, end);
    table[i] = table[i - 1] + prev.distanceTo(pt);
    prev = pt;
  }
  return table;
}

/**
 * Returns the cached arc-length table for a wall, creating it on first access.
 * Cache is keyed by "wallId:geometryVersion" — invalidated on wall geometry changes.
 */
function getArcLengthTable(wall: WallType): number[] {
  if (wall.lineForm !== "arc" || !wall.controlPoint) return [];
  const key = `${wall.id}:${wall.geometryVersion ?? 0}`;
  let table = arcLengthTableCache.get(key);
  if (!table) {
    table = buildArcLengthTable(wall.start, wall.controlPoint, wall.end);
    arcLengthTableCache.set(key, table);
    // Bound cache size to prevent unbounded growth in long sessions
    if (arcLengthTableCache.size > 500) {
      arcLengthTableCache.delete(arcLengthTableCache.keys().next().value!);
    }
  }
  return table;
}

/**
 * Converts a Bézier parameter t ∈ [0,1] to arc-length along the curved wall.
 * For straight walls returns t * chordLength.
 */
export function tToArcLength(wall: WallType, t: number): number {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    return Math.max(0, Math.min(1, t)) * wall.start.distanceTo(wall.end);
  }
  const table = getArcLengthTable(wall);
  const samples = table.length - 1;
  const idx = Math.max(0, Math.min(samples, t * samples));
  const lo = Math.floor(idx);
  const hi = Math.min(lo + 1, samples);
  const frac = idx - lo;
  return table[lo] + (table[hi] - table[lo]) * frac;
}

/**
 * Converts an arc-length s to Bézier parameter t ∈ [0,1] via binary search.
 * For straight walls returns s / chordLength (clamped).
 */
export function arcLengthToT(wall: WallType, s: number): number {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    const totalLength = wall.start.distanceTo(wall.end);
    return totalLength > 0 ? Math.max(0, Math.min(1, s / totalLength)) : 0;
  }
  const table = getArcLengthTable(wall);
  const totalArcLength = table[table.length - 1];
  if (totalArcLength <= 0) return 0;

  const sClamped = Math.max(0, Math.min(s, totalArcLength));
  const samples = table.length - 1;

  // Binary search for the interval containing sClamped
  let lo = 0;
  let hi = samples;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (table[mid] <= sClamped) lo = mid;
    else hi = mid;
  }

  const segLen = table[hi] - table[lo];
  if (segLen < 1e-10) return lo / samples;
  const frac = (sClamped - table[lo]) / segLen;
  return (lo + frac) / samples;
}

/**
 * Returns the total arc length of a wall:
 * - Arc walls: actual curve length.
 * - Straight walls: chord (Euclidean) length.
 */
export function getWallArcLength(wall: WallType): number {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const table = getArcLengthTable(wall);
    return table[table.length - 1];
  }
  return wall.start.distanceTo(wall.end);
}

/**
 * Evaluates a wall at the given positionFromStart (arc-length for arc walls,
 * Euclidean distance for straight walls).
 *
 * Returns:
 * - `point`: world position on the wall
 * - `tangent`: normalized direction along the wall at that position
 * - `t`: Bézier parameter (0–1)
 */
export function evaluateWallAt(
  wall: WallType,
  positionFromStart: number,
): { point: THREE.Vector2; tangent: THREE.Vector2; t: number } {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const t = arcLengthToT(wall, positionFromStart);
    const point = getQuadraticBezierPoint(t, wall.start, wall.controlPoint, wall.end);
    const rawTangent = getQuadraticBezierTangent(t, wall.start, wall.controlPoint, wall.end);
    const len = rawTangent.length();
    const tangent =
      len > 1e-6
        ? rawTangent.divideScalar(len)
        : new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
    return { point, tangent, t };
  }
  // Straight wall
  const wallDir = new THREE.Vector2().subVectors(wall.end, wall.start);
  const totalLength = wallDir.length();
  const tangent = totalLength > 0 ? wallDir.clone().divideScalar(totalLength) : new THREE.Vector2(1, 0);
  const t = totalLength > 0 ? Math.max(0, Math.min(1, positionFromStart / totalLength)) : 0;
  const point = wall.start.clone().addScaledVector(tangent, positionFromStart);
  return { point, tangent, t };
}

/**
 * Shared arc/Bezier utility functions for curved wall support.
 */

/** Interpolate a point on a quadratic Bezier curve at parameter t */
export function getQuadraticBezierPoint(t: number, p0: THREE.Vector2, p1: THREE.Vector2, p2: THREE.Vector2): THREE.Vector2 {
  const invT = 1 - t;
  const x = invT * invT * p0.x + 2 * invT * t * p1.x + t * t * p2.x;
  const y = invT * invT * p0.y + 2 * invT * t * p1.y + t * t * p2.y;
  return new THREE.Vector2(x, y);
}

/** Tangent of a quadratic Bezier curve at parameter t: B'(t) = 2(1-t)(P1-P0) + 2t(P2-P1) */
export function getQuadraticBezierTangent(t: number, p0: THREE.Vector2, p1: THREE.Vector2, p2: THREE.Vector2): THREE.Vector2 {
  const invT = 1 - t;
  const x = 2 * invT * (p1.x - p0.x) + 2 * t * (p2.x - p1.x);
  const y = 2 * invT * (p1.y - p0.y) + 2 * t * (p2.y - p1.y);
  return new THREE.Vector2(x, y);
}

/**
 * Calculates the approximate arc length of a quadratic Bezier curve
 * using numerical integration by sampling the curve.
 */
export function calculateArcLength(start: THREE.Vector2, controlPoint: THREE.Vector2, end: THREE.Vector2): number {
  // For straight lines (controlPoint at midpoint), use direct distance
  const midpoint = start.clone().add(end).multiplyScalar(0.5);
  if (controlPoint.distanceTo(midpoint) < 0.001) {
    return start.distanceTo(end);
  }

  const samples = 100;
  let arcLength = 0;
  let prevPoint = start.clone();

  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const point = getQuadraticBezierPoint(t, start, controlPoint, end);
    arcLength += prevPoint.distanceTo(point);
    prevPoint = point;
  }

  return arcLength;
}

/** Returns normalized tangent at t=0 (endpoint "start") or t=1 (endpoint "end") for an arc wall */
export function getArcTangentAtEndpoint(wall: WallType, endpoint: "start" | "end"): THREE.Vector2 {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    // Fallback to chord direction for straight walls
    return new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
  }
  const t = endpoint === "start" ? 0 : 1;
  const tangent = getQuadraticBezierTangent(t, wall.start, wall.controlPoint, wall.end);
  const len = tangent.length();
  if (len < 1e-6) {
    return new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
  }
  return tangent.normalize();
}

/**
 * Find the closest point on a quadratic Bezier curve to a given point.
 * Uses iterative subdivision for accuracy.
 * Returns the parameter t, the closest point on the curve, and the distance.
 */
export function projectPointOntoArc(
  point: THREE.Vector2,
  start: THREE.Vector2,
  controlPoint: THREE.Vector2,
  end: THREE.Vector2
): { t: number; point: THREE.Vector2; distance: number } {
  // Coarse pass: sample 50 points
  let bestT = 0;
  let bestDist = Infinity;
  let bestPoint = start.clone();
  const coarseSamples = 50;

  for (let i = 0; i <= coarseSamples; i++) {
    const t = i / coarseSamples;
    const p = getQuadraticBezierPoint(t, start, controlPoint, end);
    const d = point.distanceTo(p);
    if (d < bestDist) {
      bestDist = d;
      bestT = t;
      bestPoint = p;
    }
  }

  // Fine pass: refine around bestT
  const refinePasses = 3;
  let rangeHalf = 1 / coarseSamples;
  for (let pass = 0; pass < refinePasses; pass++) {
    const lower = Math.max(0, bestT - rangeHalf);
    const upper = Math.min(1, bestT + rangeHalf);
    const fineSamples = 20;
    for (let i = 0; i <= fineSamples; i++) {
      const t = lower + (upper - lower) * (i / fineSamples);
      const p = getQuadraticBezierPoint(t, start, controlPoint, end);
      const d = point.distanceTo(p);
      if (d < bestDist) {
        bestDist = d;
        bestT = t;
        bestPoint = p;
      }
    }
    rangeHalf /= fineSamples;
  }

  return { t: bestT, point: bestPoint, distance: bestDist };
}

/**
 * Computes the default control point for an arc wall given start, end, and a bulge amount.
 * The control point is placed at the midpoint of the chord, offset perpendicular by `bulge`.
 *
 * For a quadratic Bezier, the control point offset from the chord midpoint is 2x
 * the maximum curve deviation, so we multiply bulge by 2 to get the actual CP offset.
 */
export function getDefaultControlPoint(start: THREE.Vector2, end: THREE.Vector2, bulge: number): THREE.Vector2 {
  const mid = start.clone().add(end).multiplyScalar(0.5);
  const chord = new THREE.Vector2().subVectors(end, start);
  const chordLen = chord.length();
  if (chordLen < 1e-6) return mid;
  // Perpendicular to chord (rotated 90 degrees CCW)
  const perp = new THREE.Vector2(-chord.y, chord.x).normalize();
  // For quadratic Bezier, CP offset = 2 * max curve deviation from chord
  return mid.add(perp.multiplyScalar(bulge * 2));
}

/**
 * Returns the signed perpendicular distance (bulge) of a wall's controlPoint
 * from the chord midpoint. Positive = left side of chord direction.
 */
export function getArcBulge(object: Pick<WallType, "start" | "end" | "controlPoint">): number {
  if (!object.controlPoint) return 0;
  const mid = object.start.clone().add(object.end).multiplyScalar(0.5);
  const chord = new THREE.Vector2().subVectors(object.end, object.start);
  const chordLen = chord.length();
  if (chordLen < 1e-6) return 0;
  const perp = new THREE.Vector2(-chord.y, chord.x).normalize();
  const cpOffset = new THREE.Vector2().subVectors(object.controlPoint, mid);
  // Signed distance: positive when CP is on the left side of chord
  // Divide by 2 since CP offset = 2 * bulge for quadratic Bezier
  return cpOffset.dot(perp) / 2;
}

/**
 * Reprojects an arc control point into the resized wall's local frame.
 *
 * This applies the same rotate/scale transform as the wall chord itself so
 * endpoint drags preserve the arc's relative shape instead of leaving the
 * control point behind in world space.
 */
export function transformArcControlPoint(
  controlPoint: THREE.Vector2,
  oldStart: THREE.Vector2,
  oldEnd: THREE.Vector2,
  newStart: THREE.Vector2,
  newEnd: THREE.Vector2
): THREE.Vector2 {
  const oldChord = new THREE.Vector2().subVectors(oldEnd, oldStart);
  const newChord = new THREE.Vector2().subVectors(newEnd, newStart);
  const oldLength = oldChord.length();
  const newLength = newChord.length();

  if (oldLength < 1e-6 || newLength < 1e-6) {
    return newStart.clone().add(newEnd).multiplyScalar(0.5);
  }

  const oldMidpoint = oldStart.clone().add(oldEnd).multiplyScalar(0.5);
  const newMidpoint = newStart.clone().add(newEnd).multiplyScalar(0.5);
  const oldDirection = oldChord.multiplyScalar(1 / oldLength);
  const newDirection = newChord.multiplyScalar(1 / newLength);
  const oldPerpendicular = new THREE.Vector2(-oldDirection.y, oldDirection.x);
  const newPerpendicular = new THREE.Vector2(-newDirection.y, newDirection.x);
  const localOffset = controlPoint.clone().sub(oldMidpoint);
  const alongChord = localOffset.dot(oldDirection);
  const normalOffset = localOffset.dot(oldPerpendicular);
  const scale = newLength / oldLength;

  return newMidpoint
    .clone()
    .addScaledVector(newDirection, alongChord * scale)
    .addScaledVector(newPerpendicular, normalOffset * scale);
}
